import Udesk from 'Udesk';
import UdeskLocales from 'UdeskLocales';
import React, { useEffect } from 'react';
import { useRequest } from 'src/util/hook';
import { Form, Switch, Select, Input, Modal, Tag, Space, Tooltip, Button, message } from 'udesk-ui';
import { QuestionCircleOutlined } from '@ant-design/icons';
import Ellipsis from 'src/components/common/Ellipsis';
import { postSmartTagIgnoresSync } from 'src/api/smartTagIgnores/sync/index';

const FormLabelStyle = {
    width: 'calc(100% - 12px)',
};

const DisplayQuantity = {
    Unlimited: -1,
    Top5: 5,
    Top10: 10,
    Top15: 15,
};

function AutoGenerateTagsModal({ visible, onCancel }) {
    const [form] = Form.useForm();
    const [inputValue, setInputValue] = React.useState('');
    const [ignoreTagList, setignoreTagList] = React.useState([]);
    const [isAutoGenerate, setIsAutoGenerate] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const { run: getSettings } = useRequest({
        autoUpdateData: false,
        onSuccess(resp) {
            const autoGenerate = !!resp.data.isAutoGenerate;
            setIsAutoGenerate(autoGenerate);
            form.setFieldsValue({
                isAutoGenerate: autoGenerate,
                isDefaultValid: !!resp.data.isDefaultValid,
                displayQuantity: resp.data.displayQuantity ?? DisplayQuantity.Unlimited,
                requirements: resp.data.requirements,
            });
            setignoreTagList(resp.data.ignoreTagList || []);
        },
    });

    const { run: updateSettings } = useRequest({
        autoUpdateData: false,
        onSuccess() {
            Udesk.ui.notify.success(UdeskLocales['current'].business.info.saveSuccess);
            setIsSubmitting(false);
            onCancel();
        },
        onError() {
            setIsSubmitting(false);
        },
    });

    useEffect(() => {
        if (visible) {
            getSettings('/smartTags/model/setting');
        }
    }, [getSettings, visible]);

    const handleInputKeyDown = (e) => {
        if (e.key === 'Enter' && inputValue.trim()) {
            e.preventDefault();
            if (!ignoreTagList.includes(inputValue.trim())) {
                setignoreTagList([...ignoreTagList, inputValue.trim()]);
            }
            setInputValue('');
        }
    };

    const handleRemoveTag = (removedTag) => {
        setignoreTagList(ignoreTagList.filter((tag) => tag !== removedTag));
    };

    const handleOk = async () => {
        try {
            setIsSubmitting(true);
            const values = await form.validateFields();
            const params = {
                isAutoGenerate: values.isAutoGenerate ? 1 : 0,
                isDefaultValid: values.isDefaultValid ? 1 : 0,
                displayQuantity: values.displayQuantity,
                requirements: values.requirements,
                ignoreTagList: ignoreTagList,
            };
            await updateSettings('/smartTags/model/setting', params, 'put');
        } catch (error) {
            setIsSubmitting(false);
        }
    };

    const handleSwitchChange = (checked) => {
        setIsAutoGenerate(checked);
        form.setFieldsValue({ isAutoGenerate: checked });
    };

    const handleBatchFilter = () => {
        postSmartTagIgnoresSync().then(() => {
            message.success(
                UdeskLocales['current'].pages.intelligentAnalysis.ignoreLabel.index
                    .operationSuccessful
            );
        });
    };

    return (
        <Modal
            visible={visible}
            onCancel={onCancel}
            onOk={handleOk}
            confirmLoading={isSubmitting}
            title={
                <Space>
                    {
                        UdeskLocales['current'].pages.intelligentAnalysis.intelligentTags
                            .automaticallyGenerateLabels
                    }
                    <Tooltip
                        title={`${
                            /* 开启后，基于大模型对会话自动生成标签，生成的标签默认归类到"大模型标签"下 */ UdeskLocales[
                                'current'
                            ].pages.intelligentAnalysis.intelligentTags.components
                                .autoGenerateTagsModal
                                .afterOpeningTagsAreAutomaticallyGeneratedForSessionsBasedOnLargeModelsTheGeneratedTagsAreClassifiedUnderLargeModelTagsByDefault
                        }`}
                    >
                        <QuestionCircleOutlined />
                    </Tooltip>
                    <Switch checked={isAutoGenerate} onChange={handleSwitchChange} />
                </Space>
            }
            width={600}
        >
            <Form form={form} layout="horizontal" labelCol={{ span: 6 }} wrapperCol={{ span: 18 }}>
                <Form.Item
                    label={
                        <Ellipsis
                            text={
                                /* 标签要求 */ UdeskLocales['current'].pages.intelligentAnalysis
                                    .intelligentTags.components.autoGenerateTagsModal
                                    .labelingRequirements
                            }
                            style={FormLabelStyle}
                        />
                    }
                    name="requirements"
                >
                    <Input.TextArea
                        placeholder={
                            /* 请输入 */ UdeskLocales['current'].pages.intelligentAnalysis
                                .intelligentTags.components.autoGenerateTagsModal.pleaseEnter
                        }
                        rows={3}
                    />
                </Form.Item>

                <Form.Item name="isAutoGenerate" noStyle>
                    <Input type="hidden" />
                </Form.Item>

                <Form.Item
                    label={
                        <Ellipsis
                            text={
                                UdeskLocales['current'].pages.intelligentAnalysis.intelligentTags
                                    .defaultValidity
                            }
                            style={FormLabelStyle}
                        />
                    }
                    name="isDefaultValid"
                    valuePropName="checked"
                >
                    <Switch />
                </Form.Item>

                <Form.Item
                    label={
                        <Ellipsis
                            text={
                                UdeskLocales['current'].pages.intelligentAnalysis.intelligentTags
                                    .labelDisplayQuantity
                            }
                            style={FormLabelStyle}
                        />
                    }
                    name="displayQuantity"
                >
                    <Select
                        options={[
                            {
                                label: UdeskLocales['current'].pages.intelligentAnalysis
                                    .intelligentTags.unlimited,
                                value: DisplayQuantity.Unlimited,
                            },
                            {
                                label: 'top5',
                                value: DisplayQuantity.Top5,
                            },
                            {
                                label: 'top10',
                                value: DisplayQuantity.Top10,
                            },
                            {
                                label: 'top15',
                                value: DisplayQuantity.Top15,
                            },
                        ]}
                    />
                </Form.Item>

                <Form.Item
                    label={
                        <Ellipsis
                            text={
                                /* 已忽略标签 */ UdeskLocales['current'].pages.intelligentAnalysis
                                    .intelligentTags.components.autoGenerateTagsModal.tagIgnored
                            }
                            style={FormLabelStyle}
                        />
                    }
                >
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <div style={{ width: '100%', display: 'flex' }}>
                            <Input
                                style={{ flex: 1, marginRight: 8 }}
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyDown={handleInputKeyDown}
                                placeholder={
                                    /* 请输入需忽略得标签，回车自动添加 */ UdeskLocales['current']
                                        .pages.intelligentAnalysis.intelligentTags.components
                                        .autoGenerateTagsModal
                                        .pleaseEnterTheLabelToBeIgnoredAndEnterTheCarToBeAddedAutomatically
                                }
                            />
                            <Button type="primary" onClick={handleBatchFilter}>
                                {
                                    /* 批量过滤标签 */ UdeskLocales['current'].pages
                                        .intelligentAnalysis.intelligentTags.components
                                        .autoGenerateTagsModal.batchFilterLabel
                                }
                            </Button>
                        </div>
                        <Space>
                            <div style={{ flex: 1 }}>
                                {ignoreTagList.map((tag) => (
                                    <Tag
                                        key={tag}
                                        closable
                                        onClose={() => handleRemoveTag(tag)}
                                        style={{
                                            margin: '0 8px 8px 0',
                                            backgroundColor: '#f0f5ff',
                                            borderColor: '#d6e4ff',
                                            color: '#2f54eb',
                                        }}
                                    >
                                        {tag}
                                    </Tag>
                                ))}
                            </div>
                        </Space>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
}

export default AutoGenerateTagsModal;
